import { React, useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Card,
  Checkbox,
  FormControlLabel,
  Button,
  Grid,
  CircularProgress,
  Autocomplete,
  useMediaQuery,
  Chip,
} from "@mui/material";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fieldStyles } from "services/utils";
import { saveBatchWorkload, updateBatchWorkload } from "services/api";
import SnackbarWrapper from "components/Snackbar";

const EmployeeWorkload = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const employeeWorkloads = location.state?.employeeWorkloads || [];
  const jobContractualHours = location.state?.contractualHours ?? null;

  console.log("Received Employee Workloads:", employeeWorkloads);
  // 2. Set the default employee as the first person in the list
  const defaultEmployee = employeeWorkloads.length > 0 ? employeeWorkloads[0] : null;

  // 3. Local state for the Autocomplete
  const [selectedEmp, setSelectedEmp] = useState(defaultEmployee);
  const [contractualHours, setContractualHours] = useState(jobContractualHours);

  const WORKING_DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday"];
  const WEEKEND_DAYS = ["Friday", "Saturday"];
  const ALL_WEEK_DAYS = [...WORKING_DAYS, ...WEEKEND_DAYS];
  const [visibleDays, setVisibleDays] = useState(WORKING_DAYS);
  const [activeIndex, setActiveIndex] = useState(0);
  const isArabic = i18n.language === "ar";

  const startX = useRef(0); // where swipe started
  const currentX = useRef(0); // where finger/mouse currently is
  const isDragging = useRef(false); // are we dragging/swiping right now?
  const [dragging, setDragging] = useState(false); // used for animation control
  const snackbarRef = useRef();
  const navigate = useNavigate();

  const isMobile = useMediaQuery("(max-width:600px)");
  const device = isMobile ? "mobile" : "desktop";
  const [loadingSave, setLoadingSave] = useState(false);
  const [noWorkWeekendDays, setNoWorkWeekendDays] = useState({ Friday: false, Saturday: false }); // for weekend days usage

  const WORKING_FIELDS = [
    { key: "conventional" },
    { key: "planning" },
    { key: "development" },
    { key: "scheduled" },
    { key: "adhoc" },
    { key: "sporadic" },
    { key: "breaks" },
  ];

  const WEEKEND_FIELDS = [
    { key: "unconventional" },
    { key: "unscheduled" },
    { key: "development" },
    { key: "adhoc" },
    { key: "sporadic" },
    { key: "breaks" },
  ];

  const [workloadData, setWorkloadData] = useState(() =>
    // Use ALL_WEEK_DAYS here so the state has Friday and Saturday from the start
    ALL_WEEK_DAYS.reduce((acc, day) => {
      acc[day] = {
        id: null,
        conventional: "",
        planning: "",
        scheduled: "",
        development: "",
        sporadic: "",
        breaks: "",
        adhoc: "",
        unconventional: "",
        unscheduled: "",
      };
      return acc;
    }, {})
  );

  //Runs when the user first touches or clicks
  const handleStart = (x) => {
    startX.current = x; //Save where the swipe/drag began (save position)
    currentX.current = x; //Initialize "current position" to the same value
    isDragging.current = true; //Mark that dragging is now in progress
    setDragging(true); //Update React state (used for animations)
  };

  const handleMove = (x) => {
    if (!isDragging.current) return;
    currentX.current = x;
  };

  //Runs when the user releases finger/mouse
  const handleEnd = () => {
    if (!isDragging.current) return;
    const diff = currentX.current - startX.current; //Calculate the horizontal distance moved.

    if (diff > 50) {
      // swipe right
      setActiveIndex(
        (prev) => (prev === 0 ? visibleDays.length - 1 : prev - 1) // if at Sunday → go to Thursday
      );
    } else if (diff < -50) {
      // swipe left
      setActiveIndex(
        (prev) => (prev === visibleDays.length - 1 ? 0 : prev + 1) // if at Thursday → go to Sunday
      );
    }
    isDragging.current = false;
    setDragging(false);
  };

  const handleNext = () => {
    setWorkloadData((prev) => {
      const updated = { ...prev };
      visibleDays.forEach((day) => {
        WORKING_FIELDS.forEach((field) => {
          if (updated[day][field.key] === "") updated[day][field.key] = "0";
        });
      });
      return updated;
    });

    setVisibleDays(WEEKEND_DAYS); // Switch using the constant
    setActiveIndex(0);
  };

  // 3. EFFECT: When selectedEmp changes, update the contractual hours + workload
  useEffect(() => {
    if (selectedEmp) {
      // Set the value from index 7 of the selected employee array
      // 2. Get the Workload array from index 8
      const empWorkloads = selectedEmp.workloads || [];
      const draftWorkloadData = ALL_WEEK_DAYS.reduce((acc, dayName) => {
        // Match by making both sides UPPERCASE
        const record = empWorkloads.find(
          (item) => item.day.toUpperCase() === dayName.toUpperCase()
        );

        // Map the backend properties to your frontend state keys
        acc[dayName] = {
          id: record?.id ?? null,
          conventional: record?.conventionalDuties ?? "",
          planning: record?.weeklyPlanning ?? "",
          scheduled: record?.scheduledMeetings ?? "",
          development: record?.development ?? "",
          sporadic: record?.periodialAdhoc ?? "",
          breaks: record?.breaks ?? "",
          adhoc: record?.adhocMeetingsCalls ?? "",
          unconventional: record?.unconventionalDuties ?? "",
          unscheduled: record?.unscheduledMeetings ?? "",
        };

        return acc;
      }, {});
      setWorkloadData(draftWorkloadData);
    } else {
      setContractualHours("");
    }
  }, [selectedEmp]);

  const handleSubmit = async () => {
    setLoadingSave(true);
    // 1. Ensure all fields for the current view (Weekend) are zero-filled
    const updatedData = { ...workloadData };
    WEEKEND_DAYS.forEach((day) => {
      WEEKEND_FIELDS.forEach((field) => {
        if (updatedData[day][field.key] === "") updatedData[day][field.key] = "0";
      });
    });

    try {
      const allPayloads = ALL_WEEK_DAYS.reduce((acc, dayName) => {
        const dayData = updatedData[dayName];

        // Map the data to the database structure
        const entry = {
          id: dayData.id, // Temporarily include ID to know if it's an update
          employeeId: selectedEmp.employeeUid,
          day: dayName.toUpperCase(),
          conventionalDuties: parseFloat(dayData.conventional) || 0,
          weeklyPlanning: parseFloat(dayData.planning) || 0,
          scheduledMeetings: parseFloat(dayData.scheduled) || 0,
          development: parseFloat(dayData.development) || 0,
          periodialAdhoc: parseFloat(dayData.sporadic) || 0,
          breaks: parseFloat(dayData.breaks) || 0,
          adhocMeetingsCalls: parseFloat(dayData.adhoc) || 0,
          unconventionalDuties: parseFloat(dayData.unconventional) || 0,
          unscheduledMeetings: parseFloat(dayData.unscheduled) || 0,
        };

        // Calculate total hours for this specific day
        const totalDayHours =
          entry.conventionalDuties +
          entry.weeklyPlanning +
          entry.scheduledMeetings +
          entry.development +
          entry.periodialAdhoc +
          entry.breaks +
          entry.adhocMeetingsCalls +
          entry.unconventionalDuties +
          entry.unscheduledMeetings;

        // if update record: always do it, if add: check if the day has working hours
        if (entry.id || totalDayHours > 0) {
          acc.push(entry);
        }

        return acc;
      }, []);

      // 2. Separate Payloads, UPDATE payload: Keep the ID
      const toUpdate = allPayloads.filter((p) => p.id);
      // CREATE payload: Remove the ID key entirely from the objects
      const toCreate = allPayloads.filter((p) => !p.id).map(({ id, ...rest }) => rest); // This syntax removes 'id' and keeps the rest
      const apiCalls = [];

      if (toCreate.length > 0) apiCalls.push(saveBatchWorkload(toCreate));
      if (toUpdate.length > 0) apiCalls.push(updateBatchWorkload(toUpdate));

      const results = await Promise.all(apiCalls);

      if (results.includes("error")) {
        snackbarRef.current?.showSnackbar(t("user.myTeam.empWorkloadFailSaved"), "error");
      } else {
        navigate("/manpower/team");
      }
    } catch (error) {
      snackbarRef.current?.showSnackbar(t("user.myTeam.empWorkloadFailSaved"), "error");
    } finally {
      setLoadingSave(false); // STOP LOADING
    }
  };

  const handleNoWorkToggle = (day, isChecked) => {
    setNoWorkWeekendDays((prev) => ({ ...prev, [day]: isChecked }));

    setWorkloadData((prev) => ({
      ...prev,
      [day]: {
        ...prev[day], // Keep the existing ID!
        // Only reset the actual hour fields
        ...WEEKEND_FIELDS.reduce((acc, field) => {
          acc[field.key] = isChecked ? "0" : "";
          return acc;
        }, {}),
      },
    }));
  };

  return (
    <Box p={3}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        {t("user.myTeam.empWorkload")}
      </Typography>
      <Box mt={3}>
        <Autocomplete
          size="small"
          options={employeeWorkloads}
          getOptionLabel={(option) => option.employeeName || ""}
          value={selectedEmp}
          onChange={(event, newValue) => {
            setSelectedEmp(newValue);
          }}
          isOptionEqualToValue={(option, value) => option.employeeUid === value?.employeeUid}
          sx={{ width: { xs: "100%", sm: "350px" } }}
          renderInput={(params) => (
            <TextField {...params} label={t("user.myTeam.selectEmployee")} variant="outlined" />
          )}
        />
      </Box>
      {/* Contractual Hours Input (Below the selection) */}
      <Box mt={2} display="flex" alignItems="center" gap={1}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          {t("user.hoursCalculator.contractualHrs")}:
        </Typography>
        {contractualHours && (
          <Chip
            label={contractualHours}
            size="small"
            variant="outlined"
            sx={{
              height: "22px", // Very slim to match text height
              fontSize: "0.85rem",
              fontWeight: "bold",
              color: "#344767",
              borderColor: "rgba(0, 0, 0, 0.23)", // Matches standard TextField border color
              backgroundColor: "transparent",
              "& .MuiChip-label": {
                px: 1, // Tight padding
              },
            }}
          />
        )}
      </Box>

      {selectedEmp && (
        <Box mt={2} display="flex" flexDirection="column" alignItems="center">
          {/* 1. Title Section */}
          <Typography variant="h5" fontWeight="bold" sx={{ textAlign: "center", mb: 2 }}>
            {t("user.myTeam.actualHrs")}
          </Typography>

          {/* 3. Main Carousel Logic Area */}
          <Box
            sx={{ width: "100%", position: "relative" }}
            onTouchStart={(e) => handleStart(e.touches[0].clientX)}
            onTouchMove={(e) => handleMove(e.touches[0].clientX)}
            onTouchEnd={handleEnd}
            onMouseDown={(e) => handleStart(e.clientX)}
            onMouseMove={(e) => handleMove(e.clientX)}
            onMouseUp={handleEnd}
            onMouseLeave={handleEnd}
          >
            <Box display="flex" justifyContent="center">
              <Box
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  width: "100%",
                  maxWidth: { xs: "100%", sm: 600, md: 800 },
                  minHeight: { xs: 580, sm: 480, md: 450 },
                  margin: "0 auto",
                  boxShadow: 3,
                  borderRadius: 2,
                  touchAction: "pan-y",
                }}
              >
                {visibleDays.map((day, index) => {
                  const currentFields =
                    visibleDays.length === WORKING_DAYS.length ? WORKING_FIELDS : WEEKEND_FIELDS;
                  const isWeekendCard = visibleDays.length !== WORKING_DAYS.length;
                  return (
                    <Card
                      key={day}
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        transform: `translateX(${(index - activeIndex) * 100}%)`,
                        transition: dragging ? "none" : "transform 0.3s ease",
                        pt: 3, // top padding
                        px: { xs: 2, sm: 3, md: 4 }, // left & right padding
                        pb: { xs: 2, sm: 3, md: 4 }, // bottom padding
                        borderRadius: 2,
                        border: "2px solid transparent",
                        backgroundColor: "white",
                      }}
                    >
                      <Box
                        display="flex"
                        flexDirection="column" // ✅ Stack vertically
                        alignItems="flex-start" // ✅ Align both to the left (or right in Arabic)
                        sx={{ mb: 2 }}
                      >
                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                          {visibleDays.length === WORKING_DAYS.length
                            ? t(`user.hoursCalculator.days.${day}`)
                            : t(`user.hoursCalculator.weekendDays.${day}`)}
                        </Typography>
                        {isWeekendCard && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={noWorkWeekendDays[day] || false}
                                onChange={(e) => handleNoWorkToggle(day, e.target.checked)}
                                sx={{ color: "green", "&.Mui-checked": { color: "green" } }}
                              />
                            }
                            label={
                              <Typography variant="caption" fontWeight="600">
                                {t("user.myTeam.noWork")}
                              </Typography>
                            }
                          />
                        )}
                      </Box>
                      {/* 2 Fields Per Row Grid */}
                      <Grid container spacing={{ xs: 2, md: 3 }}>
                        {currentFields.map((field) => (
                          <Grid item xs={6} key={field.key}>
                            <Box display="flex" flexDirection="column" gap={0.5}>
                              <Typography
                                variant="caption"
                                fontWeight="600"
                                color="text.secondary"
                                sx={{
                                  height: { xs: "34px", sm: "auto" },
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {t(`user.myTeam.workloadFields.${field.key}`)}
                              </Typography>
                              <TextField
                                type="number"
                                size="small"
                                fullWidth
                                value={workloadData[day][field.key]}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val === "" || Number(val) >= 0) {
                                    setWorkloadData((prev) => ({
                                      ...prev,
                                      [day]: { ...prev[day], [field.key]: val },
                                    }));
                                  }
                                }}
                                inputProps={{ min: 0 }}
                                sx={{
                                  "& .MuiInputBase-root": { borderRadius: 1.5 },
                                  ...fieldStyles,
                                }}
                              />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Card>
                  );
                })}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 20, // Distance from bottom
                    right: 20, // Distance from right
                    zIndex: 10, // Ensure it sits above the cards
                    display: "flex",
                    gap: 1.5,
                  }}
                >
                  {loadingSave ? (
                    // 1. SHOW LOADING SPINNER
                    <Box display="flex" justifyContent="center" p={1}>
                      <CircularProgress size={20} sx={{ color: "black" }} />
                    </Box>
                  ) : visibleDays.length === WORKING_DAYS.length ? (
                    // Show ONLY Next when in Working Days mode
                    <Button
                      variant="outlined"
                      onClick={handleNext}
                      sx={(theme) => ({
                        width: { xs: 70, sm: 90, md: 90 }, // Your width
                        textTransform: "none",
                        fontWeight: "bold",
                        borderRadius: 2,
                        color: theme.palette.text.primary + " !important", // ✅ same as h5
                        borderColor: "green !important",
                        "&:hover": {
                          bgcolor: "green !important",
                          color: theme.palette.text.primary + " !important",
                          borderColor: "darkgreen !important",
                        },
                        ...fieldStyles,
                      })}
                    >
                      {t("user.myTeam.next")}
                    </Button>
                  ) : (
                    // Show Back and Submit when in Weekend mode
                    <>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setVisibleDays(WORKING_DAYS);
                          setActiveIndex(0);
                        }}
                        sx={(theme) => ({
                          // ✅ SAME STYLING AS NEXT BUTTON
                          width: { xs: 70, sm: 90, md: 90 },
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: "bold",
                          color: theme.palette.text.primary + " !important", // ✅ same as h5
                          borderColor: "green !important",
                          "&:hover": {
                            bgcolor: "green !important",
                            color: theme.palette.text.primary + " !important",
                            borderColor: "darkgreen !important",
                          },
                          ...fieldStyles,
                        })}
                      >
                        {t("user.myTeam.back")}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleSubmit}
                        sx={(theme) => ({
                          width: { xs: 70, sm: 90, md: 90 },
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: "bold",
                          color: theme.palette.text.primary + " !important",
                          borderColor: "blue !important",
                          "&:hover": {
                            bgcolor: "blue !important",
                            color: "#fff !important",
                            borderColor: "darkblue !important",
                          },
                        })}
                      >
                        {t("user.myTeam.submit")}
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            </Box>

            {/* 4. Desktop Navigation Arrows */}
            <IconButton
              onClick={() =>
                setActiveIndex((p) =>
                  isArabic
                    ? p === visibleDays.length - 1
                      ? 0
                      : p + 1
                    : p === 0
                      ? visibleDays.length - 1
                      : p - 1
                )
              }
              sx={{
                position: "absolute",
                top: "50%",
                left: { xs: "1%", md: "5%", lg: "10%" },
                display: device === "desktop" ? "flex" : "none",
                bgcolor: "white",
                boxShadow: 2,
                transform: "translateY(-50%)",
                "&:hover": { bgcolor: "#f5f5f5" },
              }}
            >
              <ArrowBackIos fontSize="small" sx={{ ml: 1 }} />
            </IconButton>

            <IconButton
              onClick={() =>
                setActiveIndex((p) =>
                  isArabic
                    ? p === 0
                      ? visibleDays.length - 1
                      : p - 1
                    : p === visibleDays.length - 1
                      ? 0
                      : p + 1
                )
              }
              sx={{
                position: "absolute",
                top: "50%",
                right: { xs: "1%", md: "5%", lg: "10%" },
                display: device === "desktop" ? "flex" : "none",
                bgcolor: "white",
                boxShadow: 2,
                transform: "translateY(-50%)",
                "&:hover": { bgcolor: "#f5f5f5" },
              }}
            >
              <ArrowForwardIos fontSize="small" />
            </IconButton>

            {/* 5. Dots Navigation */}
            <Box mt={3} display="flex" justifyContent="center" gap={1}>
              {visibleDays.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  sx={{
                    width: index === activeIndex ? 10 : 8,
                    height: index === activeIndex ? 10 : 8,
                    borderRadius: "50%",
                    bgcolor: index === activeIndex ? "primary.main" : "grey.300",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                />
              ))}
            </Box>
          </Box>
          <SnackbarWrapper
            ref={snackbarRef}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          />
        </Box>
      )}
    </Box>
  );
};

export default EmployeeWorkload;
